/**
 * Chest interaction flow (HEX-39 origin; varied rewards in HEX-140).
 *
 * Mirrors the `EncounterFlow` lifecycle (start → modal mount → resolve →
 * onResolved) for unresolved `chest` tiles. A chest now rolls a varied reward
 * from a weighted table (`chestRewards.ts`) instead of a flat "+25 gold"
 * (playtest D4):
 *
 *   - **Gold** — tier-scaled by distance from origin (deeper chests pay more).
 *   - **Card** — offered beside the loadout via the shared acquisition drawer
 *     (HEX-149: place into a chosen cell, curate to make room, or take its gold
 *     value). Always a card — chests are treasure, so the drop-gate is forced
 *     open (legendary tier).
 *   - **Die** — a specialist die offered beside the loadout the same way
 *     (place-or-decline as a 1×1 `die`-card, ADR-0024).
 *   - **Flavour** — an empty chest; no reward.
 *
 * Deterministic per chest: the host seeds the reward roll from `(coords,
 * crossings)` via `chestSeed`. The reward payload is rolled ONCE at `start` so
 * the line shown is exactly the reward granted (display + grant share the seeded
 * stream). The card/die wiring is INLINE here, not factored into a shared reward
 * helper (HEX-141 touches the same surface).
 *
 * Single shot per chest. On resolve the tile is marked resolved with
 * `lifecycleState.looterCount = 1`; already-looted chests pass through.
 */

import { rollCardDrop } from '../combat/cardDrops';
import type { DieType, Rng } from '../combat/dice';
import { cloneDie, dieCard } from '../combat/diceRoster';
import { cardPrice } from '../combat/shopStock';
import type { Card } from '../combat/types';
import { type AcquisitionSession, openAcquisition } from '../combat/ui/curationDrawer';
import type { Character } from '../core/character';
import type { Tile } from '../core/tile';
import { dangerTierAt } from '../lifecycle/dangerEvent';
import type { Persistence } from '../persistence/Persistence';
import { chestGold, pickChestDie, pickChestFlavour, pickChestReward } from './chestRewards';

/** A rolled reward shown in the chest modal (card → the acquisition drawer). */
type ModalReward =
  | { readonly kind: 'gold'; readonly amount: number }
  | { readonly kind: 'die'; readonly die: DieType }
  | { readonly kind: 'flavour'; readonly text: string };

export interface ChestFlowDeps {
  readonly persistence: Pick<Persistence, 'saveCharacter' | 'saveTileMutations'>;
  /** Seeded RNG for the reward roll (host seeds from `chestSeed(tile, cross)`). */
  readonly rng: Rng;
  /**
   * World generation seed (HEX-93). Folds frontier settlements into the
   * distance-tier metric that scales the gold reward. Optional + additive.
   */
  readonly worldSeed?: number;
}

export interface ChestFlowStartOptions {
  readonly character: Character;
  readonly tile: Tile;
  readonly deps: ChestFlowDeps;
  readonly onResolved: () => void | Promise<void>;
}

export class ChestFlow {
  private modal: HTMLDivElement | null = null;
  private acquiring: AcquisitionSession | null = null;
  private active = false;

  constructor(private readonly container: HTMLElement) {}

  /**
   * Mount the chest reward flow if the tile is an unresolved chest. Returns true
   * when the flow took over (the host should set its activeOverlay flag to pause
   * world input); false otherwise.
   */
  start(opts: ChestFlowStartOptions): boolean {
    if (this.active) return false;
    if (opts.tile.type !== 'chest') return false;
    if (opts.tile.state === 'resolved') return false;
    const lifecycle = opts.tile.lifecycleState;
    if (lifecycle !== null && lifecycle.kind === 'chest' && lifecycle.looterCount > 0) {
      return false;
    }

    this.active = true;
    const def = pickChestReward(opts.deps.rng());

    if (def.kind === 'card') {
      // A chest's card slot is always a card (treasure): `rollCardDrop` at the
      // legendary tier has drop-chance 1, so it returns null only if an authored
      // rarity pool is empty — in that case fall through to a flavour beat.
      const card = rollCardDrop('legendary', opts.deps.rng);
      if (card !== null) {
        this.runCardReward(opts, card);
        return true;
      }
    }

    const kind = def.kind === 'card' ? 'flavour' : def.kind;
    this.modal = this.mountModal(opts, this.rollModalReward(opts, kind));
    return true;
  }

  isActive(): boolean {
    return this.active;
  }

  destroy(): void {
    this.modal?.remove();
    this.modal = null;
    this.acquiring?.destroy();
    this.acquiring = null;
    this.active = false;
  }

  /** Roll the gold / die / flavour payload exactly once, off the seeded stream. */
  private rollModalReward(
    opts: ChestFlowStartOptions,
    kind: 'gold' | 'die' | 'flavour',
  ): ModalReward {
    switch (kind) {
      case 'gold':
        return { kind: 'gold', amount: chestGold(this.goldTier(opts)) };
      case 'die':
        return { kind: 'die', die: cloneDie(pickChestDie(opts.deps.rng())) };
      default:
        return { kind: 'flavour', text: pickChestFlavour(opts.deps.rng()) };
    }
  }

  /** Chest modal for the gold / die / flavour rewards: show the reward text and
   * a single Close button that applies the reward and resolves the chest. */
  private mountModal(opts: ChestFlowStartOptions, reward: ModalReward): HTMLDivElement {
    const root = document.createElement('div');
    root.className = 'hex6-interaction-modal';
    root.dataset.role = 'chest-modal';
    root.dataset.reward = reward.kind;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-label', 'Chest');

    const panel = document.createElement('div');
    panel.className = 'hex6-interaction-modal__panel';

    const title = document.createElement('h2');
    title.className = 'hex6-interaction-modal__title';
    title.textContent = 'Chest';
    panel.appendChild(title);

    const body = document.createElement('div');
    body.className = 'hex6-interaction-modal__body';
    const reveal = document.createElement('span');
    reveal.dataset.role = 'chest-reward';
    reveal.textContent = describeReward(reward);
    body.appendChild(reveal);
    panel.appendChild(body);

    const actions = document.createElement('div');
    actions.className = 'hex6-interaction-modal__actions';
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.dataset.action = 'chest-close';
    closeBtn.className = 'hex6-interaction-modal__button hex6-interaction-modal__button--primary';
    closeBtn.textContent = 'Close';
    closeBtn.addEventListener('click', () => {
      void this.closeModalReward(opts, reward);
    });
    actions.appendChild(closeBtn);
    panel.appendChild(actions);

    root.appendChild(panel);
    this.container.appendChild(root);
    return root;
  }

  /** Distance tier (1–5) scaling the gold reward. */
  private goldTier(opts: ChestFlowStartOptions): number {
    return dangerTierAt(opts.tile.q, opts.tile.r, opts.deps.worldSeed);
  }

  /** Apply the rolled gold / die reward, then resolve the chest. */
  private async closeModalReward(opts: ChestFlowStartOptions, reward: ModalReward): Promise<void> {
    if (!this.active) return;
    this.modal?.remove();
    this.modal = null;

    const { character } = opts;
    if (reward.kind === 'gold') {
      character.gold += reward.amount;
    } else if (reward.kind === 'die') {
      // Offer the die beside the loadout (HEX-149): place it (1×1, ADR-0024 §2),
      // curate to make room, or leave it. Declined / no room = forgone.
      const session = openAcquisition(this.container, character, {
        item: dieCard(reward.die),
        declineLabel: 'Leave it',
      });
      this.acquiring = session;
      await session.done;
      this.acquiring = null;
    }
    await this.finalise(opts);
  }

  /** Card reward: offer the card beside the loadout (HEX-149) — place it into a
   * chosen cell, curate to make room, or take its gold value — then resolve. */
  private runCardReward(opts: ChestFlowStartOptions, card: Card): void {
    const { character } = opts;
    const goldValue = cardPrice(card);
    const session = openAcquisition(this.container, character, {
      item: card,
      declineLabel: `Take ${goldValue} gold`,
    });
    this.acquiring = session;
    void session.done.then((outcome) => {
      this.acquiring = null;
      // Card-or-gold (HEX-72): a declined card is forgone for its market value.
      if (outcome === 'declined') character.gold += goldValue;
      void this.finalise(opts);
    });
  }

  /** Mark the chest looted (one-shot), persist character + tile, fire onResolved. */
  private async finalise(opts: ChestFlowStartOptions): Promise<void> {
    if (!this.active) return;
    this.active = false;

    const { tile, deps } = opts;
    tile.state = 'resolved';
    if (tile.lifecycleState !== null && tile.lifecycleState.kind === 'chest') {
      tile.lifecycleState = { ...tile.lifecycleState, looterCount: 1 };
    }
    await deps.persistence.saveCharacter(opts.character);
    await deps.persistence.saveTileMutations(tile);
    await opts.onResolved();
  }
}

/** Reward line shown in the chest modal (non-card rewards). */
function describeReward(reward: ModalReward): string {
  switch (reward.kind) {
    case 'gold':
      return `+${reward.amount} gold`;
    case 'die':
      return `A ${reward.die.name} — slot it into your loadout.`;
    default:
      return reward.text;
  }
}
